export type ReachabilityTarget = "window" | "document";

export type ReachabilityEnvironment = {
  isOnline: () => boolean;
  isVisible: () => boolean;
  checkHealth: (signal: AbortSignal) => Promise<boolean>;
  addEventListener: (target: ReachabilityTarget, type: string, listener: () => void) => void;
  removeEventListener: (target: ReachabilityTarget, type: string, listener: () => void) => void;
  setTimer: (listener: () => void, delayMs: number) => ReturnType<typeof setTimeout>;
  clearTimer: (timer: ReturnType<typeof setTimeout>) => void;
  now: () => number;
};

export type ReachabilityOptions = {
  timeoutMs?: number;
  healthyPollMs?: number;
  retryPollMs?: number;
  interactionDebounceMs?: number;
};

export type ReachabilityStore = {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => boolean;
  checkNow: (force?: boolean) => Promise<void>;
  dispose: () => void;
};

const DEFAULTS = {
  timeoutMs: 4_000,
  healthyPollMs: 30_000,
  retryPollMs: 10_000,
  interactionDebounceMs: 2_000
};
const STORE_STOPPED_REASON = "reachability-store-stopped";

export function createReachabilityStore(
  environment: ReachabilityEnvironment,
  options: ReachabilityOptions = {}
): ReachabilityStore {
  const settings = { ...DEFAULTS, ...options };
  const listeners = new Set<() => void>();
  let apiReachable: boolean | null = null;
  let snapshot = environment.isOnline();
  let started = false;
  let lastCheckStartedAt = Number.NEGATIVE_INFINITY;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let controller: AbortController | null = null;
  let inFlight: Promise<void> | null = null;

  const publish = () => {
    const next = environment.isOnline() && apiReachable !== false;
    if (next === snapshot) return;
    snapshot = next;
    listeners.forEach((listener) => listener());
  };

  const cancelTimer = () => {
    if (timer === null) return;
    environment.clearTimer(timer);
    timer = null;
  };

  const schedule = (delayMs: number) => {
    if (!started || listeners.size === 0 || !environment.isOnline()) return;
    cancelTimer();
    timer = environment.setTimer(() => {
      timer = null;
      void checkNow();
    }, delayMs);
  };

  const checkNow = (force = false): Promise<void> => {
    if (inFlight) return inFlight;
    if (!environment.isOnline()) {
      apiReachable = false;
      publish();
      return Promise.resolve();
    }
    if (!force && !environment.isVisible()) {
      schedule(settings.healthyPollMs);
      return Promise.resolve();
    }
    if (!force && environment.now() - lastCheckStartedAt < settings.interactionDebounceMs) {
      return Promise.resolve();
    }

    cancelTimer();
    lastCheckStartedAt = environment.now();
    controller = new AbortController();
    const activeController = controller;
    const timeout = environment.setTimer(() => activeController.abort(), settings.timeoutMs);
    inFlight = environment.checkHealth(activeController.signal)
      .then((reachable) => {
        apiReachable = reachable;
      })
      .catch(() => {
        if (activeController.signal.reason !== STORE_STOPPED_REASON) {
          apiReachable = false;
        }
      })
      .finally(() => {
        const stoppedForNoSubscribers = activeController.signal.reason === STORE_STOPPED_REASON;
        environment.clearTimer(timeout);
        if (controller === activeController) controller = null;
        inFlight = null;
        publish();
        if (stoppedForNoSubscribers && started && listeners.size > 0) {
          void checkNow(true);
        } else {
          schedule(apiReachable ? settings.healthyPollMs : settings.retryPollMs);
        }
      });
    return inFlight;
  };

  const handleOffline = () => {
    cancelTimer();
    controller?.abort();
    apiReachable = false;
    publish();
  };
  const handleOnline = () => {
    apiReachable = false;
    publish();
    void checkNow(true);
  };
  const handleInteraction = () => {
    if (environment.isVisible()) void checkNow();
  };

  const start = () => {
    if (started) return;
    started = true;
    environment.addEventListener("window", "online", handleOnline);
    environment.addEventListener("window", "offline", handleOffline);
    environment.addEventListener("window", "focus", handleInteraction);
    environment.addEventListener("document", "visibilitychange", handleInteraction);
    void checkNow(true);
  };

  const stop = () => {
    if (!started) return;
    started = false;
    cancelTimer();
    controller?.abort(STORE_STOPPED_REASON);
    environment.removeEventListener("window", "online", handleOnline);
    environment.removeEventListener("window", "offline", handleOffline);
    environment.removeEventListener("window", "focus", handleInteraction);
    environment.removeEventListener("document", "visibilitychange", handleInteraction);
  };

  return {
    subscribe(listener) {
      listeners.add(listener);
      start();
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) stop();
      };
    },
    getSnapshot: () => snapshot,
    checkNow,
    dispose() {
      listeners.clear();
      stop();
    }
  };
}

export function browserReachabilityEnvironment(): ReachabilityEnvironment {
  return {
    isOnline: () => navigator.onLine,
    isVisible: () => document.visibilityState === "visible",
    checkHealth: async (signal) => {
      const response = await fetch("/api/health", {
        method: "GET",
        cache: "no-store",
        credentials: "same-origin",
        headers: { Accept: "application/json" },
        redirect: "error",
        signal
      });
      return response.ok;
    },
    addEventListener: (target, type, listener) => {
      (target === "window" ? window : document).addEventListener(type, listener);
    },
    removeEventListener: (target, type, listener) => {
      (target === "window" ? window : document).removeEventListener(type, listener);
    },
    setTimer: (listener, delayMs) => setTimeout(listener, delayMs),
    clearTimer: (timer) => clearTimeout(timer),
    now: () => Date.now()
  };
}
